import {Component, DestroyRef, inject, Input} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {AdxBookingSegmentService} from '../../service/adx-booking-segment.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {combineLatest, debounceTime, distinctUntilChanged, startWith, switchMap} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DateService} from 'src/app/shared/service/date/date.service';
import {AirportService} from 'src/app/shared/service/airport/airport-service';
import {HttpParams} from '@angular/common/http';

@Component({
    selector: 'app-adx-booking-segment-info',
    templateUrl: './adx-booking-segment-info.component.html',
    standalone: false
})

@AutoUnsubscribe()
export class AdxBookingSegmentInfoComponent {
  @Input() public data: any;
  private readonly destroyRef = inject(DestroyRef);
  _adxBookingSegment : any;
  isEdit = false;
  adxBookingSegmentForm: FormGroup = this.fb.group({});

  protected get departDateTimeControl() {
    return this.adxBookingSegmentForm.controls['departDateTime'];
  }

  protected get departTimeControl() {
    return this.adxBookingSegmentForm.controls['departTime'];
  }
  
  protected get arrivalDateTimeControl() {
    return this.adxBookingSegmentForm.controls['arrivalDateTime'];
  }

  protected get arrivalTimeControl() {
    return this.adxBookingSegmentForm.controls['arrivalTime'];
  }

  protected get departureCityControl() {
    return this.adxBookingSegmentForm.controls['departureCity'];
  }

  protected get arrivalCityControl() {
    return this.adxBookingSegmentForm.controls['arrivalCity'];
  }

  protected filteredDepartureCityOptions: any[] = [];
  protected filteredArrivalCityOptions: any[] = [];
  protected isDepartureCityReadOnly = false;
  protected isArrivalCityReadOnly = false;

  constructor(
    private fb: FormBuilder,
    private adxBookingSegmentService: AdxBookingSegmentService,
    private dateService: DateService,
    private _snackBar: MatSnackBar,
    private airportService: AirportService) {
  }

  ngOnInit(){
    this.getAdxBookingSegmentInfo();
    this.createForm();
    this.subscribeToDateChanges();
    this.setAutoCompletes();
  }

  getAdxBookingSegmentInfo() {
    if (this.data != null){
      this._adxBookingSegment = this.data;
    }
  }

  toggleEdit(): void {
    if (this.isEdit){
      this.resetForm();
    }
    this.isDepartureCityReadOnly = this.departureCityControl.value ? true : false;
    this.isArrivalCityReadOnly = this.arrivalCityControl.value ? true : false;
    this.isEdit = !this.isEdit;

    if (this.isEdit) {
      this.adxBookingSegmentForm.markAllAsTouched();
    }
  }

  createForm(): void {
    this.adxBookingSegmentForm = this.fb.group({
      airlineCode: [
        this._adxBookingSegment.airlineCode,
        [Validators.required, Validators.minLength(2), Validators.maxLength(2)]
      ],
      flightNumber: [
        this._adxBookingSegment.flightNumber,
        [Validators.required, Validators.maxLength(4)]
      ],
      departureCity: [
        this.formatAirportDisplay(this._adxBookingSegment.departureAirport),
        [Validators.required]
      ],
      departDateTime: [
        this._adxBookingSegment.departDateTime,
        Validators.required
      ],
      departTime: [
        this.dateService.returnSplittedTime(this._adxBookingSegment.departDateTime),
        Validators.required
      ],
      arrivalCity: [
        this.formatAirportDisplay(this._adxBookingSegment.arrivalAirport),
        [Validators.required]
      ],
      arrivalDateTime: [
        this._adxBookingSegment.arrivalDateTime
      ],
      arrivalTime: [
        this.dateService.returnSplittedTime(this._adxBookingSegment.arrivalDateTime)
      ],
      fareBasesCode: [
        this._adxBookingSegment.fareBasesCode,
        [Validators.minLength(1), Validators.maxLength(1)]
      ],
      fare: [
        this._adxBookingSegment.fare,
        Validators.pattern(/^[0-9]\d*(\.\d{1,2})?$/)
      ],
      classOfService: [
        this._adxBookingSegment.classOfService,
        [Validators.minLength(1), Validators.maxLength(1)]
      ],
      mileage: [
        this._adxBookingSegment.mileage,
        Validators.pattern(/^[0-9]\d*(\.\d{1,2})?$/)
      ],
      originConnectionIndicator: [
        this._adxBookingSegment.originConnectionIndicator,
        [Validators.minLength(1), Validators.maxLength(1)]
      ],
    });
  }

  resetForm(): void {
    this.adxBookingSegmentForm?.patchValue({
      airlineCode: this._adxBookingSegment.airlineCode,
      flightNumber: this._adxBookingSegment.flightNumber,
      departureCity: this.formatAirportDisplay(this._adxBookingSegment.departureAirport),
      departDateTime: this._adxBookingSegment.departDateTime,
      departTime: this.dateService.returnSplittedTime(this._adxBookingSegment.departDateTime),
      arrivalCity: this.formatAirportDisplay(this._adxBookingSegment.arrivalAirport),
      arrivalDateTime: this._adxBookingSegment.arrivalDateTime,
      arrivalTime:  this.dateService.returnSplittedTime(this._adxBookingSegment.arrivalDateTime),
      fareBasesCode: this._adxBookingSegment.fareBasesCode,
      fare: this._adxBookingSegment.fare,
      classOfService: this._adxBookingSegment.classOfService,
      mileage: this._adxBookingSegment.mileage,
      originConnectionIndicator: this._adxBookingSegment.originConnectionIndicator
    });
  }

  updateSegment(){
    const formValues = this.adxBookingSegmentForm.value;
    const updatedSegment = {
      ...this._adxBookingSegment,
      ...formValues,
      departDateTime: this.dateService.getCombinedDateTimeAsString(formValues.departDateTime, formValues.departTime),
      arrivalDateTime: this.dateService.getCombinedDateTimeAsString(formValues.arrivalDateTime, formValues.arrivalTime),
      departureCity: this.departureCityControl.value ? this.departureCityControl.value.split('-')[0].trim() : null,
      arrivalCity: this.arrivalCityControl.value ? this.arrivalCityControl.value.split('-')[0].trim() : null
    }

    this.adxBookingSegmentService.updateAdxBookingSegment(updatedSegment)
      .subscribe({
          next: (data) => {
            this._adxBookingSegment = data;
            this._snackBar.open('Successfully updated booking segment information!!', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-success']
            });
            
            this.isEdit = !this.isEdit;
          },
          error: () => {
            this._snackBar.open('Failed to update booking segment information.', 'Dismiss', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['message-position', 'app-notification-error']
            });
          }
      });
  }

  private subscribeToDateChanges() {
    combineLatest({
      departDate: this.departDateTimeControl.valueChanges.pipe(startWith(this.departDateTimeControl.value)),
      arrivalDate: this.arrivalDateTimeControl.valueChanges.pipe(startWith(this.arrivalDateTimeControl.value)),
      departTime: this.departTimeControl.valueChanges.pipe(startWith(this.departTimeControl.value)),
      arrivalTime: this.arrivalTimeControl.valueChanges.pipe(startWith(this.arrivalTimeControl.value))
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({departDate, arrivalDate, departTime, arrivalTime}) => {
        if (departDate && arrivalDate) {
          const departDateTime = departTime ? this.dateService.getCombinedDateTimeAsDate(departDate, departTime) : new Date(departDate);
          const arrivalDateTime = arrivalTime ? this.dateService.getCombinedDateTimeAsDate(arrivalDate, arrivalTime) : new Date(arrivalDate);

          const isInvalid = departDateTime && arrivalDateTime && departDateTime > arrivalDateTime;
          this.arrivalDateTimeControl.setErrors(isInvalid ? { invalidDate: true } : null);
          
          if (departTime && arrivalTime) {
            this.arrivalTimeControl.setErrors(isInvalid ? { invalidDate: true } : null);
          }
        }
    });
  }

  private setAutoCompletes(): void {
    this.departureCityControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value && value.length >= 3) {
          return this.airportService.searchPaginatedAirports(new HttpParams({fromObject: { AutoCompleteSearch: value } }))
          }
          return [];
        }))
      .subscribe(response => this.filteredDepartureCityOptions = response.items);
  
    this.arrivalCityControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(value => {
          if (value && value.length >= 3) {
          return this.airportService.searchPaginatedAirports(new HttpParams({fromObject: { AutoCompleteSearch: value } }))
          }
          return [];
        }))
      .subscribe(response => this.filteredArrivalCityOptions = response.items);
  }
  
  onDepartureCityOptionSelected(event: any): void {
    const selectedItem = event.option.value;
    this.isDepartureCityReadOnly = true;
    this.departureCityControl.setValue(selectedItem);
  }
  
  onArrivalCityOptionSelected(event: any): void {
    const selectedItem = event.option.value;
    this.isArrivalCityReadOnly = true;
    this.arrivalCityControl.setValue(selectedItem);
  }

  clearDepartureCitySelection(): void {
    this.isDepartureCityReadOnly = false;
    this.departureCityControl.setValue('');
    this.filteredDepartureCityOptions = [];
  }

  clearArrivalCitySelection(): void {
    this.isArrivalCityReadOnly = false;
    this.arrivalCityControl.setValue('');
    this.filteredArrivalCityOptions = [];
  }

  formatAirportDisplay(airport: any): string{
    if (airport !== undefined && airport !== null) {
      return airport.airportCode + ' - ' + airport.airportName + ', ' + airport.countryName
    }

    return '';
  }
}